/* eslint-disable react/no-multi-comp */
import React, { ComponentProps, useEffect, useRef, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAsync, useAsyncFn, useUpdateEffect } from 'react-use';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';

import { createAction } from '@reduxjs/toolkit';
import { Pane, postStatic } from '@yandex-int/k-common/client/api/defs/post-static';
import { putWebShare } from '@yandex-int/k-common/client/api/defs/put-web-share';
import cx from 'classnames';
import { Link as LinkIcon, Refresh, ShareArrow } from 'education-icons';
import { v4 as uuid } from 'uuid';

import { retrieveFromSessionStorage, saveToSessionStorage } from '../../utils/web-storage';
import { copyTextToClipboard } from '../../utils/copy-text-to-clipboard/copy-text-to-clipboard';
import { useStableCallback, useOnBeforeUnload } from '../../utils/hooks';
import { useAppContext } from '../app-factory/context';
import { Button } from '../button/button';
import { useIntl } from '../intl/context';
import { TooltipStateful } from '../LegoTooltip/LegoTooltip';
import { MarkdownExtXContent } from '../markdown/markdown';
import { showNetworkError } from '../notification/actions';
import { RobotCodeLoadable } from '../robot/__code/robot__code-loadable';
import { monospace } from '../robot/font';
import SplitView from '../split-view/split-view';
import { getMe } from '../user/selectors';

import { WebProblemError } from './__error/web-problem__error';
import { ProblemWebAnswer, WebProblemProps, WebTestError } from './types';
import { useWindowMessage } from './useWindowMessage';
// @ts-ignore
import styles from './web-problem.module.scss';

const createHtmlPane = (htmlCode: string): Pane => ({
    content: htmlCode,
    language: 'html',
    name: 'index.html',
});

const trainerLibUrl = 'https://code.s3.yandex.net/trainer_libs/brackets.js';

export const webVerdictAction = createAction<Pick<ProblemWebAnswer, 'verdict'>>('WEB_PROBLEM/VERDICT');

export let refreshAndRunTests: () => void;

const Web = (props: WebProblemProps) => {
    const frameRef = useRef<HTMLIFrameElement>(null);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const postMessage = (message: unknown) =>
        frameRef.current?.contentWindow?.postMessage(JSON.stringify(message), '*');

    const me = useSelector(getMe);

    /** Ключ для кода в sessionStorage */
    const sessionKey = `web-${me?.id}-${JSON.stringify(useParams())}`;

    const [code, setCodeRaw] = useState(
        () =>
            retrieveFromSessionStorage(sessionKey) ||
            (props.attempt?.markers?.user_answer.panes || props.problem.markup.panes)[0].content
    );

    // Подтверждаем закрытие вкладки, если был обновлен код
    const [shouldConfirmExit, setShouldConfirmExit] = useState(false);
    useOnBeforeUnload(shouldConfirmExit);

    /** Синхронизирует код с sessionStorage и спрашивает подтверждение перед закрытием вкладки */
    const setCode = (newCode: string) => {
        saveToSessionStorage(sessionKey, newCode);
        setCodeRaw(newCode);
        setShouldConfirmExit(true);
    };

    const [frameTitle, setFrameTitle] = useState<string>();
    const [errors, setErrors] = useState<WebTestError[]>(() => {
        const attemptErrors = props.attempt?.markers?.user_answer.verdict.errors;
        if (attemptErrors) {
            return attemptErrors.map((e) => {
                try {
                    if (e[0] === '{') {
                        return JSON.parse(e);
                    }
                } catch {
                    //
                }
                return e;
            });
        }
        return [];
    });

    const initialCode = props.showCorrectAnswers ? props.problem.markup.answer_panes[0].content : code;
    const [iframeContent, setIframeContent] = useState<{ panes: Pane[]; test?: boolean }>(() => ({
        panes: [createHtmlPane(initialCode)],
    }));
    useUpdateEffect(() => {
        setIframeContent({ panes: [createHtmlPane(initialCode)] });
    }, [props.showCorrectAnswers]);
    const { waitMessage } = useWindowMessage((data) => {
        if (typeof data.externalLink === 'string') {
            window.open(data.externalLink, '_blank')?.focus();
        }
    });
    const { feynmanDomain } = useAppContext();
    const uploadCode = useAsync(() => postStatic(iframeContent.panes), [iframeContent]);
    const key = uploadCode.value?.key;
    const refreshFrame = useAsync(async () => {
        if (key && iframeLoaded) {
            postMessage({
                // не работает без ts, неправильно парсится в feynman/frame.js
                // [...new URLSearchParams('https://feynman.praktikum.yandex.net:443/static/300249e9-b09d-4c83-ba0e-3f252782f8f0/index.html?ts=1632413856692&liburl=https://code.s3.yandex.net:443/trainer_libs/brackets.js').keys()]
                // = ["https://feynman.praktikum.yandex.net:443/static/300249e9-b09d-4c83-ba0e-3f252782f8f0/index.html?ts", "liburl"]
                src: `https://${feynmanDomain}/static/${key}/index.html?ts=${Date.now()}&liburl=${encodeURI(
                    trainerLibUrl
                )}`,
                staticFilesUrl: `https://${feynmanDomain}/staticfiles`,
            });
            const { title } = await waitMessage<{ title: string | undefined }>((data) => data.onload);
            setFrameTitle(title);

            if (iframeContent.test) {
                postMessage({
                    panes: iframeContent.panes,
                    test: props.problem.markup.test,
                    answer_panes: props.problem.markup.answer_panes,
                });
                const { result } = await waitMessage<{ result: WebTestError[] }>((data) => Array.isArray(data.result));
                setErrors(result);
                props.dispatch?.(
                    webVerdictAction({
                        verdict: {
                            status: result.length === 0,
                            errors: (result as WebTestError[]).map((e) =>
                                typeof e === 'string' ? e : JSON.stringify(e)
                            ),
                        },
                    })
                );
            }
        }
    }, [iframeLoaded, key]);
    const runTests = useStableCallback(() => setIframeContent({ panes: [createHtmlPane(code)], test: true }));
    useEffect(() => {
        refreshAndRunTests = runTests;
    }, [runTests]);
    const readOnly = props.readOnly === true;
    const isFrameLoading = !iframeLoaded || uploadCode.loading || refreshFrame.loading;
    const canShare = props.me?.id && typeof props.problemLinkId !== 'undefined';
    const [linkShared, setLinkShared] = useState<string | null>(null);
    useEffect(() => setLinkShared(null), [iframeContent]);
    const [shareState, requestShare] = useAsyncFn(async () => {
        const response = await putWebShare({
            panes: iframeContent.panes,
            // problemLinkId не может быть undefined, если canShare
            problemLinkId: props.problemLinkId!,
        });
        const indexFile = response.find((f) => f.name === 'index.html');
        if (indexFile) {
            copyTextToClipboard(indexFile.url);
            setLinkShared(indexFile.url);
        }
    }, [iframeContent.panes, props.problemLinkId]);
    const dispatch = useStableCallback(props.dispatch);
    useEffect(() => {
        if (shareState.error) {
            dispatch?.(showNetworkError(shareState.error));
        }
    }, [dispatch, shareState.error]);

    return (
        <SplitView className={styles.web} minSizes={[350, 350]}>
            <div className={styles.left}>
                <SplitView className={styles.web} direction="column" initialSizes={[2, 4]}>
                    <RobotCodeLoadable
                        className={cx(styles.code, { [styles.code_teacher]: props.teacherMode })}
                        fontSize="13px"
                        initialValue={initialCode}
                        key={props.showCorrectAnswers ? 'answer' : undefined}
                        onChange={
                            readOnly
                                ? undefined
                                : (_, __, value) => {
                                      setCode(value);
                                      props.setAnswer?.({
                                          markerAnswer: {
                                              user_answer: { panes: [createHtmlPane(value)] },
                                          },
                                      });
                                  }
                        }
                        options={{
                            mode: 'htmlmixed',
                            indentUnit: 2,
                            cursorScrollMargin: 0,
                            readOnly: readOnly || undefined,
                        }}
                        paddingBottom={0}
                        paddingRight={12}
                    />
                    <div>
                        {errors.length > 0 && !props.showCorrectAnswers && <WebProblemError error={errors[0]} />}
                        <div className={styles.info}>
                            <h3 className={styles.instruction}>{props.problem.markup.instruction}</h3>
                            <MarkdownExtXContent>{props.problem.markup.description}</MarkdownExtXContent>
                        </div>
                    </div>
                </SplitView>
            </div>
            <div>
                <div className={cx(styles.browser, { [styles.browser_teacher]: props.teacherMode })}>
                    <div className={styles.browser__controls}>
                        <BrowserBtn
                            content="problemWeb.refresh"
                            disabled={isFrameLoading || readOnly}
                            icon={<Refresh />}
                            onClick={() => setIframeContent({ panes: [createHtmlPane(code)] })}
                        />
                        <div className={styles.browser__address}>
                            {frameTitle || <FormattedMessage id="problemWeb.browser" />}
                            {linkShared && (
                                <Button
                                    accent={Button.ACCENT.NORMAL}
                                    as="a"
                                    // @ts-ignore
                                    href={linkShared}
                                    icon={<ShareArrow />}
                                    iconPosition={Button.ICON_POSITION.RIGHT}
                                    rounded
                                    size={Button.SIZE.S}
                                    target="_blank"
                                    view={Button.VIEW.PSEUDO}
                                >
                                    <FormattedMessage id="problemWeb.openLink" />
                                </Button>
                            )}
                        </div>
                        {canShare && (
                            <BrowserBtn
                                content={linkShared ? 'problemWeb.linkCopied' : 'problemWeb.copyLink'}
                                disabled={isFrameLoading || shareState.loading}
                                icon={<LinkIcon />}
                                onClick={requestShare}
                            />
                        )}
                    </div>
                    <div className={styles.browser__content}>
                        <iframe
                            allow="fullscreen;geolocation;camera;microphone;publickey-credentials-get"
                            className={styles.browser__frame}
                            frameBorder="0"
                            onLoad={() => setIframeLoaded(true)}
                            ref={frameRef}
                            src={`https://${feynmanDomain}/staticfiles/frame.html`}
                        />
                    </div>
                </div>
            </div>
        </SplitView>
    );
};

const BrowserBtn = ({ content, ...props }: ComponentProps<typeof Button> & { content: string }) => {
    const intl = useIntl();
    return (
        <TooltipStateful
            content={intl.formatMessage({ id: content })}
            direction="top"
            hasTail
            mainOffset={16}
            view="default"
        >
            <Button rounded={true} size={Button.SIZE.M} view={Button.VIEW.RAISED} {...props} />
        </TooltipStateful>
    );
};

export const WebProblem = (props: WebProblemProps) => {
    // в статистике учителя не сразу приезжает attempt внутри problem. костыль key чтоб показать ответ ученика
    const key = useMemo(uuid, [props.problem]);
    if (props.previewMode) {
        return <div className={cx(styles.preview, monospace)}>{props.problem.markup.panes[0].content}</div>;
    }
    return <Web {...props} key={key} />;
};
